import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Sparkles, Coins, CreditCard, ChevronDown, ChevronUp, Plus, ArrowLeftRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { CreditTransferDialog } from "./credit-transfer-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const ROLES = ["admin", "sales_rep", "artist", "billing", "marketing"] as const;
const ROLE_LABELS: Record<string, string> = {
  admin: "Admin", sales_rep: "Sales Rep", artist: "Talent", billing: "Billing", marketing: "Marketing",
};

interface CreditConfig {
  mode: string;
  payer: string;
  rolloverEnabled: boolean;
  rolloverCapMultiplier: number;
  overagePolicy: string;
  negativeLimitCents?: number;
  negativeLimitCount?: number;
  defaultByRole: Record<string, number>;
}
interface CreditConfigResponse { ai: CreditConfig; lead: CreditConfig; }
interface UserAllocation {
  userId: string; displayName: string; email: string; role: string;
  allocations: Record<string, { monthlyAllotment: number | null; currentBalance: number; rolloverBalance: number; }>;
}

export function CreditDistributionCard({ creditType }: { creditType: "ai" | "lead" }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [expandUsers, setExpandUsers] = useState(false);
  const [topupUser, setTopupUser] = useState<string | null>(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [editAllotment, setEditAllotment] = useState<{ userId: string; value: string } | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const { data: cfg, isLoading } = useQuery<CreditConfigResponse>({
    queryKey: ["/api/admin/credit-config"],
  });
  const { data: users, isLoading: loadingUsers } = useQuery<UserAllocation[]>({
    queryKey: ["/api/admin/credit-config/users"],
    enabled: expandUsers,
  });

  const patchMutation = useMutation({
    mutationFn: async (patch: Record<string, any>) => {
      const res = await apiRequest("PATCH", "/api/admin/credit-config", patch);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config"] });
      toast({ title: "Saved" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const topupMutation = useMutation({
    mutationFn: async ({ userId, amount }: { userId: string; amount: number }) => {
      const res = await apiRequest("POST", `/api/admin/credit-config/users/${userId}/${creditType}/topup`, { amount });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config/users"] });
      setTopupUser(null);
      setTopupAmount("");
      toast({ title: "Top-up applied" });
    },
    onError: () => toast({ title: "Top-up failed", variant: "destructive" }),
  });

  const allotmentMutation = useMutation({
    mutationFn: async ({ userId, monthlyAllotment }: { userId: string; monthlyAllotment: number | null }) => {
      const res = await apiRequest("PATCH", `/api/admin/credit-config/users/${userId}/${creditType}`, { monthlyAllotment });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config/users"] });
      setEditAllotment(null);
      toast({ title: "Allotment updated" });
    },
    onError: () => toast({ title: "Failed to update allotment", variant: "destructive" }),
  });

  const provisionMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("POST", `/api/admin/credit-config/users/${userId}/${creditType}/provision`, {});
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config/users"] });
      toast({ title: "Member provisioned" });
    },
    onError: () => toast({ title: "Failed to provision member", variant: "destructive" }),
  });

  const c = cfg?.[creditType];
  const isAi = creditType === "ai";
  const title = isAi ? "AI Credit Distribution" : "Lead Credit Distribution";
  const unit = isAi ? "¢" : " credits";
  const negLimit = isAi ? c?.negativeLimitCents : c?.negativeLimitCount;

  function patch(field: string, value: any) {
    patchMutation.mutate({ [creditType]: { [field]: value } });
  }

  if (isLoading) return (
    <Card><CardContent className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></CardContent></Card>
  );
  if (!c) return null;

  return (
    <Card data-testid={`card-credit-distribution-${creditType}`}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {isAi ? <Sparkles className="w-5 h-5 text-primary" /> : <Coins className="w-5 h-5 text-primary" />}
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>Control how {isAi ? "AI" : "lead"} credits are allocated across your team</CardDescription>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => setTransferOpen(true)}
                    disabled={c.mode === "shared"}
                    data-testid={`button-transfer-credits-${creditType}`}
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    Transfer
                  </Button>
                </span>
              </TooltipTrigger>
              {c.mode === "shared" && (
                <TooltipContent>
                  Switch to a per-user distribution mode to enable credit transfers.
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CreditTransferDialog open={transferOpen} onOpenChange={setTransferOpen} creditType={creditType} />
      <CardContent className="space-y-6">

        <div className="space-y-2">
          <Label className="text-sm font-medium">Distribution mode</Label>
          <Select value={c.mode} onValueChange={(v) => patch("mode", v)}>
            <SelectTrigger className="w-full max-w-xs" data-testid={`select-${creditType}-credit-mode`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="shared">Shared workspace pool</SelectItem>
              <SelectItem value="per_user_monthly">Per user — monthly allotment</SelectItem>
              <SelectItem value="per_user_allocated">Per user — fixed allocation</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {c.mode === "shared" && "All members draw from one shared workspace credit pool."}
            {c.mode === "per_user_monthly" && "Each member gets a monthly allotment. Unused credits roll over (capped)."}
            {c.mode === "per_user_allocated" && "Admin assigns a fixed total to each member. No automatic refresh."}
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">Who purchases credits</Label>
          <Select value={c.payer} onValueChange={(v) => patch("payer", v)}>
            <SelectTrigger className="w-full max-w-xs" data-testid={`select-${creditType}-credit-payer`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="workspace">Workspace (admin buys credits)</SelectItem>
              <SelectItem value="user">Each user buys their own</SelectItem>
            </SelectContent>
          </Select>
          {c.payer === "user" && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Users will see a "Buy credits" button and must purchase their own credits to use {isAi ? "AI features" : "lead gen"}.
            </p>
          )}
        </div>

        {c.mode === "per_user_monthly" && (
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Rollover unused credits</Label>
                <p className="text-xs text-muted-foreground">Carry forward unused monthly credits instead of expiring them</p>
              </div>
              <Switch
                data-testid={`switch-${creditType}-rollover`}
                checked={c.rolloverEnabled}
                onCheckedChange={(v) => patch("rolloverEnabled", v)}
              />
            </div>
            {c.rolloverEnabled && (
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Rollover cap (× monthly allotment)</Label>
                <Input
                  type="number"
                  className="w-24"
                  min={1}
                  max={12}
                  step={0.5}
                  defaultValue={c.rolloverCapMultiplier}
                  data-testid={`input-${creditType}-rollover-cap`}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v >= 1) patch("rolloverCapMultiplier", v);
                  }}
                />
                <span className="text-xs text-muted-foreground">e.g. 3× = max 3 months stored</span>
              </div>
            )}
          </div>
        )}

        {c.mode !== "shared" && (
          <div className="space-y-3 rounded-lg border p-4">
            <Label className="text-sm font-medium">When a user runs out of credits</Label>
            <Select value={c.overagePolicy} onValueChange={(v) => patch("overagePolicy", v)}>
              <SelectTrigger className="w-full max-w-xs" data-testid={`select-${creditType}-overage`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hard_stop">Block the action (hard stop)</SelectItem>
                <SelectItem value="fallback_shared">Fall back to shared workspace pool</SelectItem>
                <SelectItem value="allow_negative">Allow negative balance (mid-session grace)</SelectItem>
              </SelectContent>
            </Select>
            {c.overagePolicy === "allow_negative" && (
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">
                  Max negative {isAi ? "balance (¢)" : "credits"}
                </Label>
                <Input
                  type="number"
                  className="w-28"
                  min={0}
                  defaultValue={negLimit ?? (isAi ? 50000 : 500)}
                  data-testid={`input-${creditType}-negative-limit`}
                  onBlur={(e) => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v) && v >= 0) {
                      patch(isAi ? "negativeLimitCents" : "negativeLimitCount", v);
                    }
                  }}
                />
                {isAi && <span className="text-xs text-muted-foreground">= ${((negLimit ?? 50000) / 100).toFixed(0)} max debt</span>}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {c.overagePolicy === "hard_stop" && "Actions that require credits will be blocked when the user's balance reaches zero."}
              {c.overagePolicy === "fallback_shared" && "Once a user's allotment is exhausted, spend falls through to the workspace pool."}
              {c.overagePolicy === "allow_negative" && "Mid-session actions (long scrapes, coaching) can push the balance negative. User is prompted to top up afterwards."}
            </p>
          </div>
        )}

        {c.mode !== "shared" && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Default monthly allotment per role</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Set a baseline for each role. Individual users inherit this unless overridden below. 0 = unlimited.
            </p>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>{isAi ? "Monthly (¢)" : "Monthly (credits)"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ROLES.map((role) => (
                    <TableRow key={role}>
                      <TableCell className="font-medium">{ROLE_LABELS[role]}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="w-28 h-7 text-sm"
                          min={0}
                          defaultValue={c.defaultByRole?.[role] ?? ""}
                          placeholder="inherit"
                          data-testid={`input-${creditType}-role-${role}`}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value);
                            const updated = { ...(c.defaultByRole ?? {}) };
                            if (e.target.value === "" || isNaN(v)) {
                              delete updated[role];
                            } else {
                              updated[role] = v;
                            }
                            patch("defaultByRole", updated);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {c.mode !== "shared" && (
          <div className="space-y-3">
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
              onClick={() => setExpandUsers(!expandUsers)}
              data-testid={`button-${creditType}-expand-users`}
            >
              {expandUsers ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Per-user overrides
            </button>

            {expandUsers && (
              <div className="rounded-md border overflow-hidden">
                {loadingUsers ? (
                  <div className="p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>{c.mode === "per_user_monthly" ? "Monthly allotment" : "Total allocation"}</TableHead>
                        <TableHead>Current balance</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(users ?? []).map((u) => {
                        const alloc = u.allocations[creditType];
                        const hasRow = !!alloc;
                        const isEditing = editAllotment?.userId === u.userId;
                        return (
                          <TableRow key={u.userId} data-testid={`row-user-allotment-${u.userId}`}>
                            <TableCell>
                              <div>
                                <div className="font-medium text-sm">{u.displayName}</div>
                                <div className="text-xs text-muted-foreground">{u.email}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{ROLE_LABELS[u.role] ?? u.role}</Badge>
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    className="w-24 h-7 text-sm"
                                    min={0}
                                    value={editAllotment.value}
                                    onChange={(e) => setEditAllotment({ userId: u.userId, value: e.target.value })}
                                    data-testid={`input-allotment-${u.userId}`}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-7 px-2"
                                    disabled={allotmentMutation.isPending}
                                    onClick={() => {
                                      const v = editAllotment.value === "" ? null : parseInt(editAllotment.value);
                                      allotmentMutation.mutate({ userId: u.userId, monthlyAllotment: v });
                                    }}
                                    data-testid={`button-save-allotment-${u.userId}`}
                                  >
                                    {allotmentMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditAllotment(null)}>✕</Button>
                                </div>
                              ) : (
                                <button
                                  className="text-sm hover:underline text-left"
                                  onClick={() => setEditAllotment({ userId: u.userId, value: String(alloc?.monthlyAllotment ?? "") })}
                                  data-testid={`button-edit-allotment-${u.userId}`}
                                >
                                  {alloc?.monthlyAllotment !== null && alloc?.monthlyAllotment !== undefined
                                    ? `${alloc.monthlyAllotment}${unit}`
                                    : <span className="text-muted-foreground italic">role default</span>}
                                </button>
                              )}
                            </TableCell>
                            <TableCell>
                              {hasRow ? (
                                <>
                                  <span className={cn(
                                    "text-sm font-mono",
                                    (alloc?.currentBalance ?? 0) < 0 ? "text-destructive" : "text-foreground"
                                  )}>
                                    {alloc?.currentBalance ?? 0}{unit}
                                  </span>
                                  {(alloc?.rolloverBalance ?? 0) > 0 && (
                                    <span className="ml-1 text-xs text-muted-foreground">({alloc!.rolloverBalance}{unit} rolled)</span>
                                  )}
                                </>
                              ) : (
                                <span className="text-xs text-muted-foreground italic" data-testid={`text-unprovisioned-${u.userId}`}>
                                  Not provisioned
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {topupUser === u.userId ? (
                                <div className="flex items-center gap-1 justify-end">
                                  <Input
                                    type="number"
                                    className="w-24 h-7 text-sm"
                                    min={1}
                                    placeholder="amount"
                                    value={topupAmount}
                                    onChange={(e) => setTopupAmount(e.target.value)}
                                    data-testid={`input-topup-${u.userId}`}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-7 px-2"
                                    disabled={topupMutation.isPending || !topupAmount}
                                    onClick={() => topupMutation.mutate({ userId: u.userId, amount: Number(topupAmount) })}
                                    data-testid={`button-confirm-topup-${u.userId}`}
                                  >
                                    {topupMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setTopupUser(null); setTopupAmount(""); }}>✕</Button>
                                </div>
                              ) : hasRow ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => setTopupUser(u.userId)}
                                  data-testid={`button-topup-${u.userId}`}
                                >
                                  <Plus className="w-3 h-3 mr-1" />Top up
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  disabled={provisionMutation.isPending}
                                  onClick={() => provisionMutation.mutate(u.userId)}
                                  data-testid={`button-provision-${u.userId}`}
                                >
                                  {provisionMutation.isPending && provisionMutation.variables === u.userId
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <><Plus className="w-3 h-3 mr-1" />Provision</>}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(users ?? []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                            No members found. Allocations are provisioned automatically when members join, or use the Provision action above.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function UserCreditBalanceCard() {
  const { toast } = useToast();

  const { data: balance, isLoading } = useQuery<any>({
    queryKey: ["/api/me/credit-balance"],
    staleTime: 30_000,
  });

  const aiCheckoutMutation = useMutation({
    mutationFn: async (priceCents: number) => {
      const res = await apiRequest("POST", "/api/user-credits/ai/checkout", { priceCents });
      return res.json();
    },
    onSuccess: (data) => { if (data.url) window.location.href = data.url; },
    onError: () => toast({ title: "Checkout failed", variant: "destructive" }),
  });

  const leadCheckoutMutation = useMutation({
    mutationFn: async (priceCents: number) => {
      const res = await apiRequest("POST", "/api/user-credits/lead/checkout", { priceCents });
      return res.json();
    },
    onSuccess: (data) => { if (data.url) window.location.href = data.url; },
    onError: () => toast({ title: "Checkout failed", variant: "destructive" }),
  });

  const aiPacks = [
    { priceCents: 1000,  label: "Starter — $10" },
    { priceCents: 2500,  label: "Standard — $25 (+8%)" },
    { priceCents: 5000,  label: "Pro — $50 (+12%)" },
    { priceCents: 10000, label: "Bulk — $100 (+20%)" },
  ];
  const leadPacks = [
    { priceCents: 2500,  label: "Starter — $25 (250 credits)" },
    { priceCents: 5000,  label: "Standard — $50 (550 credits)" },
    { priceCents: 10000, label: "Pro — $100 (1,200 credits)" },
    { priceCents: 25000, label: "Bulk — $250 (3,500 credits)" },
  ];

  const showAiBuy = balance?.ai?.payer === "user";
  const showLeadBuy = balance?.lead?.payer === "user";

  if (!showAiBuy && !showLeadBuy) return null;
  if (isLoading) return null;

  return (
    <Card data-testid="card-user-credit-balance">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">My Credits</CardTitle>
            <CardDescription>Your personal credit balances</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showAiBuy && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> AI Credits
              </div>
              <div className={cn("text-lg font-bold", (balance?.ai?.perUser?.currentBalance ?? 0) < 0 ? "text-destructive" : "")}>
                {balance?.ai?.perUser?.currentBalance !== undefined
                  ? `${(balance.ai.perUser.currentBalance / 100).toFixed(2)}`
                  : "—"}
              </div>
            </div>
            <Select onValueChange={(v) => aiCheckoutMutation.mutate(Number(v))}>
              <SelectTrigger className="w-44" data-testid="select-buy-ai-credits">
                <SelectValue placeholder="Buy credits" />
              </SelectTrigger>
              <SelectContent>
                {aiPacks.map((p) => (
                  <SelectItem key={p.priceCents} value={String(p.priceCents)}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {showLeadBuy && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                <Coins className="w-4 h-4 text-primary" /> Lead Credits
              </div>
              <div className={cn("text-lg font-bold", (balance?.lead?.perUser?.currentBalance ?? 0) < 0 ? "text-destructive" : "")}>
                {balance?.lead?.perUser?.currentBalance !== undefined
                  ? `${balance.lead.perUser.currentBalance} credits`
                  : "—"}
              </div>
            </div>
            <Select onValueChange={(v) => leadCheckoutMutation.mutate(Number(v))}>
              <SelectTrigger className="w-44" data-testid="select-buy-lead-credits">
                <SelectValue placeholder="Buy credits" />
              </SelectTrigger>
              <SelectContent>
                {leadPacks.map((p) => (
                  <SelectItem key={p.priceCents} value={String(p.priceCents)}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
